export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  phone?: string;
  CUIT: string;
  roles: string[];
  status: string;
}

const API_BASE_URL = (typeof window !== 'undefined' && (window as any).ENV?.EXPO_PUBLIC_USER_API_URL) || '';

export async function fetchUserProfile(token: string): Promise<UserProfile> {
  const response = await fetch(`${API_BASE_URL}/user/profile`, {
    headers: {
      "Authorization": `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error("Failed to fetch user profile");
  }

  const result = await response.json();
  return result.data;
}

export async function fetchAllUsers(
  token: string,
  page?: number,
  limit?: number,
  search?: string
): Promise<any> {
  let url = `${API_BASE_URL}/user/all`;
  const params = new URLSearchParams();
  if (page !== undefined) params.append("page", String(page));
  if (limit !== undefined) params.append("limit", String(limit));
  if (search !== undefined && search.trim() !== "") params.append("search", search);

  const queryStr = params.toString();
  if (queryStr) {
    url += "?" + queryStr;
  }

  const response = await fetch(url, {
    headers: {
      "Authorization": `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error("Failed to fetch all users");
  }

  const result = await response.json();
  return result.data;
}

export async function fetchActiveProducersCount(token: string): Promise<number> {
  const response = await fetch(`${API_BASE_URL}/user/producers/active/count`, {
    headers: {
      "Authorization": `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error("Failed to fetch active producers count");
  }

  const result = await response.json();
  return result.data.count;
}


export async function updateUserProfile(
  profileData: { full_name: string; phone: string },
  token: string
): Promise<UserProfile> {
  const response = await fetch(`${API_BASE_URL}/user/profile`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify(profileData)
  });

  if (!response.ok) {
    throw new Error("Failed to update user profile");
  }

  const result = await response.json();
  return result.data;
}

export async function updateUserById(
  userId: string,
  profileData: Partial<{ full_name: string; phone: string; CUIT: string; email: string }>,
  token: string
): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/user/${userId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify(profileData)
  });

  if (!response.ok) {
    throw new Error(`Failed to update user ${userId}`);
  }

  const result = await response.json();
  return result.data;
}


export async function createUser(
  userData: { email: string; full_name: string; CUIT: string; role?: string; phone?: string },
  token: string
): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/user/create`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify(userData)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to create user");
  }

  const result = await response.json();
  return result.data;
}

export async function toggleUserStatus(
  userId: string,
  status: "ACTIVE" | "INACTIVE",
  token: string
): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/user/${userId}/status`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({ status })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to update user status");
  }

  return response.json();
}

export async function deleteUser(
  userId: string,
  token: string
): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/user/${userId}`, {
    method: "DELETE",
    headers: {
      "Authorization": `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to delete user");
  }

  return response.json();
}


export async function assignUserRole(
  userId: string,
  role: string,
  token: string
): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/user/${userId}/role`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({ role })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to assign role ${role} to user ${userId}`);
  }

  return response.json();
}

export async function removeUserRole(
  userId: string,
  role: string,
  token: string
): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/user/${userId}/role`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({ role })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to remove role ${role} from user ${userId}`);
  }

  return response.json();
}

export async function changeUserRole(
  userId: string,
  oldRole: string,
  newRole: string,
  token: string
): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/user/${userId}/role`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({ old_role: oldRole, new_role: newRole })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to change role from ${oldRole} to ${newRole} for user ${userId}`);
  }

  return response.json();
}

export async function fetchUserById(userId: string, token: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/user/${userId}`, {
    headers: {
      "Authorization": `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user with ID ${userId}`);
  }

  const result = await response.json();
  return result.data;
}

export async function seedDatabase(): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/db/seed`, {
    method: "POST"
  });

  if (!response.ok) {
    throw new Error("Failed to seed database");
  }

  return response.json();
}



